import { equals } from 'ramda';
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { EditBoardModal } from '../../../../components/common/BoardModals/EditBoardModal';
import { IBoardState } from '../../../../components/common/BoardModals/EditBoardModal/EditBoardModal';
import { MediaManagerSDKBoardSitesVisibility } from '../../../../constants/BoardSitesVisibility';
import { ModalName } from '../../../../constants/ModalName';
import { MainSource } from '../../../../constants/Source';
import { createNewBoardNotification } from '../../../../containers/FilesManager/notifications';
import { IWithFolderPathInner } from '../../../hoc/withFolderPath/withFolderPath.types';
import { IWithItemsReducerInner } from '../../../hoc/withItemsReducer/withItemsReducer.types';
import { MediaManagerSDKAnyItem, MediaManagerSDKBoard, MediaManagerSDKFolder } from '../../../media-manager-sdk';
import { getUniqueDefaultBoardNameIndex, isBoard } from '../../../media-manager-sdk/boards';
import { CreateBoardPopUpTarget, useTrackBoardAnalytics } from '../../bi/useTrackBoardAnalytics';
import { useBoards } from '../../useBoards';
import { useMediaManagerSDK } from '../../useMediaManagerSDK';
import { useNotificatorConsumer } from '../../useNotificator';
import { useScrollStream } from '../../useScrollStream';
import { useSources } from '../../useSources';
import { useModal, useModalHandlersAndOptions } from '../useModal';

interface IUseEditBoardModal {
  source: MainSource;
  prependItems: IWithItemsReducerInner['prependItems'];
  selectItem: IWithItemsReducerInner['selectItem'];
  updateLocalItem: IWithItemsReducerInner['updateLocalItem'];
  currentFolder: MediaManagerSDKFolder;
  updateFolderInPath: IWithFolderPathInner['updateFolderInPath'];
}

export const useEditBoardModal = ({
  source,
  prependItems,
  selectItem,
  updateLocalItem,
  currentFolder,
  updateFolderInPath,
}: IUseEditBoardModal) => {
  const { formatMessage } = useIntl();
  const { emitScroll } = useScrollStream();
  const { isBoardsRoot } = useSources({ source });
  const { boards, updateBoards } = useBoards();
  const { createNewBoardMP, updateBoardMP } = useMediaManagerSDK({ source, currentFolder });

  const { notify } = useNotificatorConsumer();
  const { trackCreateBoardPopUpClick, trackBoardSaved } = useTrackBoardAnalytics();
  const {
    closeModal: closeEditBoardModal,
    additionalOptions: { clickOrigin },
  } = useModalHandlersAndOptions(ModalName.EditBoard);

  const { addItems } = useBoards();

  const handleCreateBoard = useCallback(
    async (newBoardOptions, isCoverChanged, selectedItems) => {
      const newBoard = await createNewBoardMP(newBoardOptions);

      if (isBoardsRoot) {
        prependItems([newBoard]);
        selectItem(newBoard.id);
        setTimeout(() => emitScroll(newBoard.id), 0);
      }

      trackBoardSaved({
        boardId: newBoard.id,
        isNew: true,
        coverAdded: isCoverChanged,
        clickOrigin,
        teamAvailability: newBoard.teamAvailability,
        availability: MediaManagerSDKBoardSitesVisibility.All,
      });

      updateBoards();

      if (selectedItems) {
        addItems([newBoard], selectedItems, { clickOrigin });
      } else {
        notify(createNewBoardNotification({ board: newBoard }));
      }
    },
    [
      clickOrigin,
      createNewBoardMP,
      prependItems,
      emitScroll,
      selectItem,
      isBoardsRoot,
      trackBoardSaved,
      notify,
      updateBoards,
      addItems,
    ],
  );

  const handleUpdateBoard = useCallback(
    async (updatedOptions, isCoverChanged) => {
      const updatedBoard = await updateBoardMP(updatedOptions);

      trackBoardSaved({
        boardId: updatedBoard.id,
        isNew: false,
        coverAdded: isCoverChanged,
        clickOrigin,
        teamAvailability: updatedBoard.teamAvailability,
        availability: MediaManagerSDKBoardSitesVisibility.All,
      });

      updateBoards();

      updateLocalItem(updatedBoard.id, updatedBoard);
      selectItem(updatedBoard.id);

      if (isBoard(currentFolder)) {
        updateFolderInPath((currentFolder as MediaManagerSDKBoard).id, updatedBoard);
      }
    },
    [clickOrigin, updateBoardMP, updateLocalItem, updateFolderInPath, selectItem, currentFolder, updateBoards],
  );

  const handleSubmitBoard = useCallback(
    (board: MediaManagerSDKBoard, updatedBoardData: IBoardState, selectedItems: MediaManagerSDKAnyItem[]) => {
      const isCoverChanged = Boolean(updatedBoardData.fileThumbnail);

      const boardDefaultNamePrefix = formatMessage({ id: 'boards.create-new-board.default.name.prefix' });
      const updatedOptions = {
        ...board,
        ...updatedBoardData,
        name:
          updatedBoardData.name ||
          formatMessage(
            {
              id: 'boards.create-new-board.default.name',
            },
            {
              boardDefaultName: boardDefaultNamePrefix,
              index: getUniqueDefaultBoardNameIndex(boards, boardDefaultNamePrefix),
            },
          ),
      };

      const isNew = !updatedOptions.id;

      trackCreateBoardPopUpClick(isNew ? CreateBoardPopUpTarget.Create : CreateBoardPopUpTarget.Edit);

      if (equals(board, updatedOptions)) {
        return;
      }

      return isNew
        ? handleCreateBoard(updatedOptions, isCoverChanged, selectedItems)
        : handleUpdateBoard(updatedOptions, isCoverChanged);
    },
    [boards, handleUpdateBoard, handleCreateBoard, formatMessage],
  );

  const handleCancel = useCallback(() => {
    trackCreateBoardPopUpClick(CreateBoardPopUpTarget.Cancel);
    closeEditBoardModal();
  }, [trackCreateBoardPopUpClick, closeEditBoardModal]);

  return useModal(ModalName.EditBoard, {
    title: ({ additionalOptions: { board } }) =>
      board ? formatMessage({ id: 'boards.modal-edit-title' }) : formatMessage({ id: 'boards.modal-create-new-title' }),
    content: ({ additionalOptions: { board, selectedItems }, closeModal }) => (
      <EditBoardModal
        board={board}
        onSubmit={handleSubmitBoard}
        onCancel={handleCancel}
        closeModal={closeModal}
        selectedItems={selectedItems}
      />
    ),
    onCloseWithCloseButtonClick: () => trackCreateBoardPopUpClick(CreateBoardPopUpTarget.Close),
    hideFooter: true,
    customLayout: true,
    width: '632px',
  });
};
